import { LINK_TYPE_QUICK } from '../../../../../shared/constants';
import type { IAdLinkTemplateRules } from '../../../../../shared/types/links/Interfaces/template/ad-link-template/IAdLinkTemplateRules';
import type { IQuickLinkTemplateRules } from '../../../../../shared/types/links/Interfaces/template/quick-link-template/IQuickLinkTemplateRules';
import { deepCopy } from '../../../../../shared/utils';
import { LinkDataService } from '../LinkDataService';
import { RedirectTypeMappingService } from '../RedirectTypeMappingService';
import { INITIAL_SCRAPING_BEHAVIOR } from './constants';

export default class OriginalWebUrlScrapingService {
  private scrapingData;

  private templateRules;

  private scrapingBehavior;

  constructor(currentLink, scrapingData: unknown, templateRules?: IQuickLinkTemplateRules | IAdLinkTemplateRules) {
    this.scrapingData = scrapingData;
    this.templateRules = templateRules;
    this.scrapingBehavior = deepCopy(INITIAL_SCRAPING_BEHAVIOR);

    if (!this.scrapingData) return;

    this.buildTitle(currentLink.title);
    this.buildRedirectDesktop();
    this.buildLinkData(currentLink.linkData, currentLink.type);
    if (currentLink.type === LINK_TYPE_QUICK) {
      this.buildSocialMedia();
    }
  }

  private buildTitle = (currentLinkTitle) => {
    if (currentLinkTitle === undefined && this.scrapingData.title) {
      this.scrapingBehavior.nameLink.title.update = true;
      this.scrapingBehavior.nameLink.title.value = this.scrapingData.title;
    }
  };

  private buildRedirectDesktop = () => {
    const canonicalUrl = this.scrapingData.link_data.$canonical_url;

    if (!canonicalUrl) return;

    const redirectDesktopBehavior = { update: true, type: 'web', value: canonicalUrl };

    if (!this.templateRules) {
      this.scrapingBehavior.redirects.desktop = redirectDesktopBehavior;
      return;
    }

    const templateDesktopRedirects = this.templateRules.redirects.desktop;
    const { redirectType, valueType, value } = templateDesktopRedirects;
    const mappedRedirectType = RedirectTypeMappingService.getMappedRedirectType(redirectType);

    if (!['web', 'any'].includes(mappedRedirectType)) return;

    switch (valueType) {
      case 'any':
      case 'suggestions':
        this.scrapingBehavior.redirects.desktop = redirectDesktopBehavior;
        return;
      case 'choose':
        if (value?.includes(canonicalUrl)) {
          this.scrapingBehavior.redirects.desktop = redirectDesktopBehavior;
        }
        return;
    }
  };

  private buildLinkData = (currentLinkData, linkType) => {
    if (!currentLinkData) return;

    let finalLinkData: any[] = [];
    const scrapedLinkCreatorData = this.scrapingData.link_data;
    const noPresetsScrapedLinkCreatorData = LinkDataService.excludePresetsFromLinkCreatorData(
      scrapedLinkCreatorData,
      linkType
    );

    if (!this.templateRules || !this.templateRules?.linkData.length) {
      const scrapedLinkData = LinkDataService.mapLinkCreatorDataToLinkDataObject(noPresetsScrapedLinkCreatorData);
      finalLinkData = LinkDataService.mergeLinkDataArraysByKey(currentLinkData, scrapedLinkData);
    } else {
      const filteredScrapedLinkCreatorData = LinkDataService.filterScrapedLinkDataWithTemplateLinkData(
        noPresetsScrapedLinkCreatorData,
        this.templateRules?.linkData
      );
      const linkData = LinkDataService.mapTemplateLinkData(filteredScrapedLinkCreatorData);
      finalLinkData = LinkDataService.mergeLinkDataArraysByKey(currentLinkData, linkData);
    }
    this.scrapingBehavior.linkData.update = true;
    this.scrapingBehavior.linkData.items = finalLinkData;
  };

  private buildSocialMedia = () => {
    const { og_title, og_description, og_image_url } = this.scrapingData.app_data;

    this.builSocialMediaItemBehavior(og_title, 'title');
    this.builSocialMediaItemBehavior(og_description, 'description');
    this.builSocialMediaItemBehavior(og_image_url, 'imageUrl');
  };

  private builSocialMediaItemBehavior = (newValue, itemName) => {
    if (!newValue) return;

    const socialMediaItemBehavior = { update: true, value: newValue };

    if (!this.templateRules) {
      this.scrapingBehavior.socialMediaTags[itemName] = socialMediaItemBehavior;
      return;
    }
    const templateSocialMediaItem = this.templateRules.socialMediaTags[itemName];

    switch (templateSocialMediaItem.valueType) {
      case 'any':
      case 'suggestions':
        this.scrapingBehavior.socialMediaTags[itemName] = socialMediaItemBehavior;
        return;
      case 'choose':
        if (templateSocialMediaItem.value?.includes(newValue)) {
          this.scrapingBehavior.socialMediaTags[itemName].shouldUpdate = socialMediaItemBehavior;
        }
        return;
    }
  };

  get linkTitleValue() {
    return this.scrapingBehavior.nameLink.title.value;
  }

  get shouldLinkTitleUpdate() {
    return this.scrapingBehavior.nameLink.title.update;
  }

  get redirectDesktopType() {
    return this.scrapingBehavior.redirects.desktop.type;
  }

  get redirectDesktopValue() {
    return this.scrapingBehavior.redirects.desktop.value;
  }

  get shouldRedirectDesktopUpdate() {
    return this.scrapingBehavior.redirects.desktop.update;
  }

  get linkDataItems() {
    return this.scrapingBehavior.linkData.items;
  }

  get shouldLinkDataUpdate() {
    return this.scrapingBehavior.linkData.update;
  }

  get socialMediaTitleValue() {
    return this.scrapingBehavior.socialMediaTags.title.value;
  }

  get shouldSocialMediaTitleUpdate() {
    return this.scrapingBehavior.socialMediaTags.title.update;
  }

  get socialMediaDescriptionValue() {
    return this.scrapingBehavior.socialMediaTags.description.value;
  }

  get shouldSocialMediaDescriptionUpdate() {
    return this.scrapingBehavior.socialMediaTags.description.update;
  }

  get socialMediaImageValue() {
    return this.scrapingBehavior.socialMediaTags.imageUrl.value;
  }

  get shouldSocialMediaImageUpdate() {
    return this.scrapingBehavior.socialMediaTags.imageUrl.update;
  }
}
